import { Controller } from "@hotwired/stimulus"
import { AnsiUp } from "ansi_up"

/**
 * Log Viewer controller - Loads and displays log files with lazy loading
 *
 * Usage:
 *   <div data-controller="log-viewer"
 *        data-log-viewer-url-value="/eden/workflow/run_steps/123"
 *        data-log-viewer-chunk-size-value="500">
 *     <div data-action="click->log-viewer#toggle">ログを表示</div>
 *     <div data-log-viewer-target="container" class="hidden">
 *       <div data-log-viewer-target="scrollArea">
 *         <div data-log-viewer-target="content"></div>
 *         <div data-log-viewer-target="sentinel"></div>
 *       </div>
 *     </div>
 *   </div>
 */
export default class extends Controller {
  static targets = ["container", "content", "spinner", "summary", "scrollArea", "sentinel", "status"]
  static values = {
    url: String,
    chunkSize: { type: Number, default: 500 },
    autoOpen: { type: Boolean, default: false }
  }

  connect() {
    this.offset = 0
    this.loaded = false
    this.loading = false
    this.hasMore = true
    this.totalLines = 0
    this.ansiUp = new AnsiUp()
    // インラインスタイルを使用（CSSクラス不要）
    this.ansiUp.use_classes = false

    this.handleMouseUp = this.copySelection.bind(this)

    if (this.autoOpenValue) {
      this.open()
    }
  }

  disconnect() {
    this.destroyObserver()
    this.teardownSelectionCopy()
    this.cleanupToast()
  }

  toggle() {
    if (this.containerTarget.classList.contains("hidden")) {
      this.open()
    } else {
      this.close()
    }
  }

  open() {
    this.containerTarget.classList.remove("hidden")
    this.updateSummaryIcon(true)
    this.setupSelectionCopy()

    if (!this.loaded) {
      this.loadChunk()
    }
  }

  close() {
    this.containerTarget.classList.add("hidden")
    this.updateSummaryIcon(false)
    this.teardownSelectionCopy()
  }

  updateSummaryIcon(isOpen) {
    if (!this.hasSummaryTarget) return

    const icon = this.summaryTarget.querySelector("i.fa-chevron-right, i.fa-chevron-down")
    if (icon) {
      icon.classList.remove("fa-chevron-right", "fa-chevron-down")
      icon.classList.add(isOpen ? "fa-chevron-down" : "fa-chevron-right")
    }
  }

  setupObserver() {
    if (this.observer || !this.hasSentinelTarget || !this.hasScrollAreaTarget) return

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && this.hasMore && !this.loading) {
            this.loadChunk()
          }
        })
      },
      {
        root: this.scrollAreaTarget,
        rootMargin: "100px",
        threshold: 0
      }
    )

    this.observer.observe(this.sentinelTarget)
  }

  destroyObserver() {
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
  }

  async loadChunk() {
    if (this.loading || !this.hasMore) return
    this.loading = true

    this.showSpinner()

    try {
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set("offset", this.offset)
      url.searchParams.set("limit", this.chunkSizeValue)

      const response = await fetch(url.toString(), {
        headers: {
          "Accept": "application/json"
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`)
      }

      const data = await response.json()

      if (data.error) {
        this.showError(data.error)
        this.hasMore = false
        return
      }

      this.appendLines(data.lines)
      this.totalLines = data.total_lines
      this.offset = data.offset + data.lines.length
      this.loaded = true
      this.hasMore = data.has_more

      // ステータス表示を更新
      this.updateStatus()

      // 初回読み込み後にObserverをセットアップ
      if (this.hasMore) {
        this.setupObserver()
      } else {
        this.destroyObserver()
        this.hideSentinel()
      }
    } catch (error) {
      console.error("Failed to load log:", error)
      this.showError("ログの読み込みに失敗しました")
      this.hasMore = false
    } finally {
      this.loading = false
      this.hideSpinner()
    }
  }

  appendLines(lines) {
    if (lines.length === 0) {
      if (this.offset === 0) {
        this.contentTarget.innerHTML = '<span class="text-gray-500">ログが空です</span>'
      }
      return
    }

    const fragment = document.createDocumentFragment()

    lines.forEach((line) => {
      const lineElement = document.createElement("div")
      lineElement.className = "log-line"
      lineElement.innerHTML = this.ansiUp.ansi_to_html(line)
      fragment.appendChild(lineElement)
    })

    this.contentTarget.appendChild(fragment)
  }

  showSpinner() {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.remove("hidden")
    }
  }

  hideSpinner() {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.add("hidden")
    }
  }

  hideSentinel() {
    if (this.hasSentinelTarget) {
      this.sentinelTarget.classList.add("hidden")
    }
  }

  updateStatus() {
    if (!this.hasStatusTarget) return

    const current = this.offset.toLocaleString()
    const total = this.totalLines.toLocaleString()
    this.statusTarget.textContent = `${current} / ${total} 行`
  }

  async jumpToEnd() {
    if (this.loading || !this.hasMore) {
      // 既に全部読み込まれている場合は最下部にスクロールするだけ
      this.scrollToBottom()
      return
    }

    this.loading = true
    this.showSpinner()

    try {
      // 最後のチャンクを取得（offsetを最後の方に設定）
      const url = new URL(this.urlValue, window.location.origin)
      // まず総行数を取得するためにlimit=1で取得
      url.searchParams.set("offset", 0)
      url.searchParams.set("limit", 1)

      const metaResponse = await fetch(url.toString(), {
        headers: { "Accept": "application/json" }
      })

      if (!metaResponse.ok) throw new Error(`HTTP error: ${metaResponse.status}`)

      const metaData = await metaResponse.json()
      if (metaData.error) {
        this.showError(metaData.error)
        return
      }

      this.totalLines = metaData.total_lines

      // 最後のチャンクを計算して取得
      const lastOffset = Math.max(0, this.totalLines - this.chunkSizeValue)

      url.searchParams.set("offset", lastOffset)
      url.searchParams.set("limit", this.chunkSizeValue)

      const response = await fetch(url.toString(), {
        headers: { "Accept": "application/json" }
      })

      if (!response.ok) throw new Error(`HTTP error: ${response.status}`)

      const data = await response.json()

      // コンテンツをクリアして最後のチャンクのみ表示
      this.contentTarget.innerHTML = ""
      this.appendLines(data.lines)
      this.offset = lastOffset + data.lines.length
      this.loaded = true
      this.hasMore = false

      this.updateStatus()
      this.destroyObserver()
      this.hideSentinel()

      // 最下部にスクロール
      this.scrollToBottom()
    } catch (error) {
      console.error("Failed to jump to end:", error)
      this.showError("ログの読み込みに失敗しました")
    } finally {
      this.loading = false
      this.hideSpinner()
    }
  }

  scrollToBottom() {
    if (this.hasScrollAreaTarget) {
      this.scrollAreaTarget.scrollTop = this.scrollAreaTarget.scrollHeight
    }
  }

  showError(message) {
    const span = document.createElement("span")
    span.className = "text-red-500"
    span.textContent = message
    this.contentTarget.replaceChildren(span)
  }

  // --- 選択テキスト自動コピー ---

  setupSelectionCopy() {
    if (!this.hasScrollAreaTarget) return
    this.scrollAreaTarget.addEventListener("mouseup", this.handleMouseUp)
  }

  teardownSelectionCopy() {
    if (!this.hasScrollAreaTarget) return
    this.scrollAreaTarget.removeEventListener("mouseup", this.handleMouseUp)
  }

  copySelection() {
    const selection = window.getSelection()
    if (!selection.rangeCount) return

    const range = selection.getRangeAt(0)
    if (!this.scrollAreaTarget.contains(range.commonAncestorContainer)) return

    const text = selection.toString()
    if (!text || text.trim().length === 0) return

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(() => {
        this.showCopyToast()
      }).catch(() => {})
    } else {
      // HTTP環境のフォールバック: 選択中テキストをそのままコピー
      try {
        document.execCommand("copy")
        this.showCopyToast()
      } catch (_) {
        // コピー不可の環境では何もしない
      }
    }
  }

  cleanupToast() {
    if (this.copyToastTimeout) {
      clearTimeout(this.copyToastTimeout)
      this.copyToastTimeout = null
    }
    if (this.existingToast) {
      this.existingToast.remove()
      this.existingToast = null
    }
  }

  showCopyToast() {
    this.cleanupToast()

    const toast = document.createElement("div")
    toast.className = "fixed top-4 right-4 bg-gray-800 text-white text-sm px-4 py-2 rounded-lg shadow-lg z-50 transform -translate-y-full opacity-0 transition-all duration-300"
    toast.textContent = "コピーしました"
    document.body.appendChild(toast)
    this.existingToast = toast

    requestAnimationFrame(() => {
      toast.classList.remove("-translate-y-full", "opacity-0")
      toast.classList.add("translate-y-0", "opacity-100")
    })

    this.copyToastTimeout = setTimeout(() => {
      toast.classList.remove("translate-y-0", "opacity-100")
      toast.classList.add("-translate-y-full", "opacity-0")
      setTimeout(() => toast.remove(), 300)
      this.existingToast = null
      this.copyToastTimeout = null
    }, 1500)
  }
}
